import React from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Lightbox } from "react-modal-image";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import CategoryCard from "../CategoryCard";
import MetricCardBlock from "./blocks/MetricCardBlock";
import MetricTableBlock from "./blocks/MetricTableBlock";
import AgentBlock from "./blocks/AgentBlock";
import SectionHeaderBlock from "./blocks/SectionHeaderBlock";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import { formatDate } from "../../requests/utilities/helpers";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../../requests/utilities/requests";
import {
  API_get_logbook_page,
  API_post_logbook_category_entry,
  API_delete_logbook_category_entry,
  API_post_logbook_block_entry,
  API_delete_logbook_block_entry,
  API_post_run_logbook_agent,
  API_get_file_upload,
} from "../../../../constants";

// A block's width is its share of the row: rows split evenly between their
// blocks (1 = full, 2 = halves, 3 = thirds, 4 = quarters), so builder and
// canvas can never disagree. Tailwind breakpoints in this codebase are
// desktop-first (md: = max-width 767px), so the unprefixed span is the
// DESKTOP width and md: stacks on mobile.
const SPAN_BY_ROW_COUNT = {
  1: "col-span-12",
  2: "col-span-6 md:col-span-12",
  3: "col-span-4 md:col-span-12",
  4: "col-span-3 md:col-span-12",
};

// The Today canvas: the company's block layout rendered for one store + date.
// The backend synthesizes a legacy layout from the two columns for companies
// that have never saved one, so this renders every company either way.
function LogbookTodayCanvas(props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [pageData, setPageData] = React.useState(null);
  const [loadFailed, setLoadFailed] = React.useState(false);

  const [showLightbox, setShowLightbox] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);

  const dateStr = formatDate(props.date);

  // Identity of the page currently rendered, assigned on EVERY render (not
  // by fetch callers): a stale closure - e.g. an entry save from the prior
  // date completing after a switch and triggering its refresh - must not be
  // able to reset this to its own old key and smuggle a stale response in
  // under the new header.
  const currentKeyRef = React.useRef("");
  currentKeyRef.current = `${props.storeId}|${dateStr}`;
  // Orders overlapping requests for the SAME page: the key alone accepts
  // both when a slow initial fetch resolves after a mutation-triggered
  // refresh, overwriting the fresher data. Only the latest-started fetch
  // may write.
  const fetchSeqRef = React.useRef(0);
  // Render-assigned mirror of pageData so fetch callbacks can ask "has
  // anything rendered for this page yet?" without a stale closure.
  const pageDataRef = React.useRef(null);
  pageDataRef.current = pageData;

  const fetchPage = async (showLoading) => {
    const requestKey = `${props.storeId}|${dateStr}`;
    // Navigation fetches define the current page; silent refreshes
    // pre-check the key BEFORE advancing the sequence - a stale closure's
    // refresh for page A bumping the counter would invalidate page B's
    // in-flight request while failing its own key check, deadlocking B on
    // the spinner.
    if (showLoading) {
      currentKeyRef.current = requestKey;
    } else if (currentKeyRef.current !== requestKey) {
      return;
    }
    const requestSeq = ++fetchSeqRef.current;
    const isCurrent = () =>
      currentKeyRef.current === requestKey &&
      fetchSeqRef.current === requestSeq;
    // A superseded request may still salvage an EMPTY page. If a silent
    // refresh bumps the sequence past an in-flight navigation load and
    // then fails, it sets no failure state (by design - it normally has a
    // valid page to keep) while the navigation response gets discarded:
    // pageData stays null and the spinner spins forever with nobody left
    // to write. So a stale response - success or failure - may write while
    // the key still matches and nothing has rendered; any newer request
    // that resolves afterwards simply overwrites it.
    const mayWrite = () =>
      isCurrent() ||
      (currentKeyRef.current === requestKey && pageDataRef.current === null);
    setIsLoading(showLoading);
    if (showLoading) {
      // Navigation fetch: never leave the PREVIOUS page's data mounted
      // under the new header - if this request fails, stale entries would
      // render (and their edit/delete handlers would act on the old day's
      // uuids). Silent refreshes (showLoading=false) keep the current page.
      setPageData(null);
      setLoadFailed(false);
      props.onResponsesOutstandingChange?.(0);
    }
    try {
      const res = await getRightworkAPI(API_get_logbook_page, {
        store_id: props.storeId,
        date: dateStr,
      });
      if (!mayWrite()) return;
      if (res.status === 200) {
        const payload = res.data?.redux?.payload;
        setPageData(payload);
        setLoadFailed(false);
        props.onResponsesOutstandingChange?.(
          payload?.responses_outstanding?.length || 0
        );
      }
    } catch (error) {
      if (!mayWrite()) return;
      // Only a navigation load renders the failure state - a failed silent
      // refresh keeps showing the (still valid) current page.
      if (showLoading) {
        setLoadFailed(true);
      }
      console.error("Failed to load logbook page", error);
    } finally {
      if (isCurrent()) {
        setIsLoading(false);
      }
    }
  };

  React.useEffect(() => {
    fetchPage(true);
  }, [dateStr, props.storeId]);

  // Silent refresh when the operations assistant creates a logbook entry
  React.useEffect(() => {
    if (
      props.opsRefreshSignal &&
      props.opsRefreshSignal.types?.includes("logbook")
    ) {
      fetchPage(false);
    }
  }, [props.opsRefreshSignal]);

  const handlePostError = (error) => {
    if (error.response?.data?.system?.message) {
      toast.error(error.response?.data?.system?.message, { autoClose: 3000 });
    } else {
      toast.error("Request Failed", { autoClose: 3000 });
    }
    console.error("Error:", error);
  };

  const get_file_upload = async (file_upload_uuid) => {
    try {
      const res = await getRightworkAPI(API_get_file_upload, {
        file_upload_uuid: file_upload_uuid,
      });
      if (res.status === 200) {
        setSelectedImage(res.data?.redux?.payload?.presigned_url);
        setShowLightbox(true);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  const post_logbook_category_entry = async (
    logbook_category_uuid,
    value_text,
    value_integer,
    value_float,
    file_upload_uuids,
    logbook_category_entry_uuid = null,
    expected_type = null
  ) => {
    const requestData = {
      store_id: props.storeId,
      logbook_category_uuid,
      value_text,
      value_integer,
      value_float,
      file_upload_uuids: JSON.stringify(file_upload_uuids),
      date: dateStr,
      ...(logbook_category_entry_uuid && { logbook_category_entry_uuid }),
      // The type this composer was built for: the server rejects the save
      // if an admin changed the category's type while the page sat open,
      // instead of stamping the entry with a type its values don't match.
      ...(expected_type && { expected_type }),
    };
    try {
      toast.loading("Saving Entry...", { autoClose: false });
      const res = await postRightworkAPI(
        API_post_logbook_category_entry,
        requestData
      );
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Entry Saved Successfully", { autoClose: 3000 });
        fetchPage(false);
      }
    } catch (error) {
      toast.dismiss();
      handlePostError(error);
    }
  };

  const delete_logbook_category_entry = async (logbook_category_entry_uuid) => {
    try {
      toast.loading("Deleting Entry...", { autoClose: false });
      const res = await postRightworkAPI(API_delete_logbook_category_entry, {
        store_id: props.storeId,
        logbook_category_entry_uuid,
      });
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Entry Deleted Successfully", { autoClose: 3000 });
        fetchPage(false);
      }
    } catch (error) {
      toast.dismiss();
      handlePostError(error);
    }
  };

  const post_logbook_block_entry = async (
    block_uuid,
    value_text,
    is_response,
    logbook_block_entry_uuid = null
  ) => {
    const requestData = {
      store_id: props.storeId,
      block_uuid,
      value_text,
      is_response,
      date: dateStr,
      ...(logbook_block_entry_uuid && { logbook_block_entry_uuid }),
    };
    try {
      toast.loading("Saving Entry...", { autoClose: false });
      const res = await postRightworkAPI(
        API_post_logbook_block_entry,
        requestData
      );
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Entry Saved Successfully", { autoClose: 3000 });
        fetchPage(false);
      }
    } catch (error) {
      toast.dismiss();
      handlePostError(error);
    }
  };

  // Keyed store|date|agent (the layout is company-wide but a run belongs
  // to one store/date): while a forced run is in flight, its block hides
  // Regenerate so a second click cannot race a duplicate LLM run.
  const [regeneratingRunKeys, setRegeneratingRunKeys] = React.useState([]);

  const run_logbook_agent = async (agent_uuid) => {
    const runKey = `${props.storeId}|${dateStr}|${agent_uuid}`;
    if (regeneratingRunKeys.includes(runKey)) {
      return;
    }
    setRegeneratingRunKeys((prev) =>
      prev.includes(runKey) ? prev : [...prev, runKey]
    );
    try {
      toast.loading("Regenerating...", { autoClose: false });
      const res = await postRightworkAPI(API_post_run_logbook_agent, {
        store_id: props.storeId,
        agent_uuid,
        date: dateStr,
      });
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Summary regenerated", { autoClose: 3000 });
        await fetchPage(false);
      }
    } catch (error) {
      toast.dismiss();
      handlePostError(error);
    } finally {
      setRegeneratingRunKeys((prev) => prev.filter((key) => key !== runKey));
    }
  };

  const delete_logbook_block_entry = async (logbook_block_entry_uuid) => {
    try {
      toast.loading("Deleting Entry...", { autoClose: false });
      const res = await postRightworkAPI(API_delete_logbook_block_entry, {
        store_id: props.storeId,
        logbook_block_entry_uuid,
      });
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Entry Deleted Successfully", { autoClose: 3000 });
        fetchPage(false);
      }
    } catch (error) {
      toast.dismiss();
      handlePostError(error);
    }
  };

  if (loadFailed) {
    return (
      <div className="w-full h-64 flex flex-col items-center justify-center">
        <p className="text-sm text-slate-500">
          The logbook could not be loaded.
        </p>
        <button
          className="mt-3 text-xs font-semibold text-violet-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5 hover:border-violet-400"
          onClick={() => fetchPage(true)}
        >
          Try again
        </button>
      </div>
    );
  }

  if (isLoading || !pageData) {
    return (
      <div className="w-full h-64 relative">
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LoadSpinner />
        </div>
      </div>
    );
  }

  const layoutRows = pageData.layout?.layout_json || [];
  const categoriesByLineage = pageData.categories_by_lineage || {};
  const metricsByBlock = pageData.metrics_by_block || {};
  const entriesByBlock = pageData.block_entries_by_block || {};
  const responsesOutstanding = new Set(pageData.responses_outstanding || []);
  const currentUser = pageData.current_user;

  const blockEntryHandlers = {
    currentUser,
    post_logbook_block_entry,
    delete_logbook_block_entry,
  };

  const renderCategoryBlock = (lineageId, key) => {
    const category = categoriesByLineage[lineageId];
    if (!category) {
      return null;
    }
    // CategoryCard carries its own mt-3 from the old stacked layout; cancel
    // it here so cards align with sibling blocks in the same grid row.
    return (
      <div key={key} className="[&>div]:mt-0">
        <CategoryCard
          fileKey={key}
          category={category}
          post_logbook_category_entry={post_logbook_category_entry}
          delete_logbook_category_entry={delete_logbook_category_entry}
          homeCompany={props.homeCompany}
          get_file_upload={get_file_upload}
          delete_file_upload={() => {}}
          currentUser={currentUser}
        />
      </div>
    );
  };

  const renderBlock = (block) => {
    if (block.type === "category") {
      return renderCategoryBlock(block.config?.lineage_id, block.uuid);
    }
    if (block.type === "metric") {
      return (
        <MetricCardBlock
          block={block}
          metric={metricsByBlock[block.uuid]}
          entries={entriesByBlock[block.uuid]}
          responseOutstanding={responsesOutstanding.has(block.uuid) && Boolean(props.canManage)}
          // Synthesized legacy blocks are not in a stored layout, so the
          // entry endpoint rejects them; hide the composer until an admin
          // saves a real layout.
          canAddEntries={!pageData.layout?.is_legacy}
          {...blockEntryHandlers}
        />
      );
    }
    if (block.type === "table") {
      return (
        <MetricTableBlock
          block={block}
          table={metricsByBlock[block.uuid]}
          entries={entriesByBlock[block.uuid]}
          responseOutstanding={responsesOutstanding.has(block.uuid) && Boolean(props.canManage)}
          {...blockEntryHandlers}
        />
      );
    }
    if (block.type === "agent") {
      const canRegenerate = currentUser?.is_admin || props.canManage;
      return (
        <AgentBlock
          block={block}
          agentName={block.config?.label}
          run={pageData.agent_runs_by_block?.[block.uuid]}
          entries={entriesByBlock[block.uuid]}
          isRegenerating={regeneratingRunKeys.includes(
            `${currentKeyRef.current}|${block.config?.agent_uuid}`
          )}
          onRegenerate={
            canRegenerate && block.config?.agent_uuid
              ? () => run_logbook_agent(block.config.agent_uuid)
              : undefined
          }
          {...blockEntryHandlers}
        />
      );
    }
    if (block.type === "section") {
      return <SectionHeaderBlock block={block} />;
    }
    return null;
  };

  const hasAnyContent = layoutRows.length > 0;

  return (
    <>
      {showLightbox && (
        <div className="absolute w-screen h-screen z-40">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}

      {!hasAnyContent ? (
        <div className="w-full h-36 text-slate-500 flex flex-col items-center justify-center border border-slate-300 rounded-xl">
          <InfoOutlinedIcon />
          <p className="mt-1 w-[65%] text-center text-sm">
            No logbook categories are configured yet. An admin can add
            categories in the Logbook Categories tab.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {layoutRows.map((row) => (
            <div key={row.uuid} className="grid grid-cols-12 gap-3 items-start">
              {row.blocks.map((block) => (
                <div
                  key={block.uuid}
                  className={`${
                    SPAN_BY_ROW_COUNT[row.blocks.length] || "col-span-3 md:col-span-12"
                  } min-w-0`}
                >
                  {renderBlock(block)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default LogbookTodayCanvas;
